import { ColumnType } from '../types/enums.js';
// ============================================================================
// Default Column Widths
// ============================================================================
/**
 * Default column width in pixels for each column type.
 */
export const DEFAULT_COLUMN_WIDTH = {
    [ColumnType.TEXT]: 200,
    [ColumnType.NUMBER]: 100,
    [ColumnType.DATE]: 120,
    [ColumnType.DATETIME]: 160,
    [ColumnType.DURATION]: 100,
    [ColumnType.PERCENT]: 80,
    [ColumnType.CURRENCY]: 120,
    [ColumnType.CHECKBOX]: 60,
    [ColumnType.DROPDOWN_SINGLE]: 150,
    [ColumnType.DROPDOWN_MULTI]: 200,
    [ColumnType.CONTACT]: 180,
    [ColumnType.PREDECESSOR]: 150,
    [ColumnType.AUTO_NUMBER]: 80,
    [ColumnType.FORMULA]: 150,
    [ColumnType.SYMBOL]: 80,
    [ColumnType.ATTACHMENT]: 100,
};
/**
 * Minimum column width in pixels.
 */
export const MIN_COLUMN_WIDTH = 40;
/**
 * Maximum column width in pixels.
 */
export const MAX_COLUMN_WIDTH = 800;
// ============================================================================
// Column Type Icons
// ============================================================================
/**
 * Icon identifiers for each column type.
 * Uses icon names compatible with common icon libraries (Lucide, Heroicons, etc.)
 */
export const COLUMN_TYPE_ICONS = {
    [ColumnType.TEXT]: 'type',
    [ColumnType.NUMBER]: 'hash',
    [ColumnType.DATE]: 'calendar',
    [ColumnType.DATETIME]: 'calendar-clock',
    [ColumnType.DURATION]: 'clock',
    [ColumnType.PERCENT]: 'percent',
    [ColumnType.CURRENCY]: 'dollar-sign',
    [ColumnType.CHECKBOX]: 'check-square',
    [ColumnType.DROPDOWN_SINGLE]: 'chevron-down-circle',
    [ColumnType.DROPDOWN_MULTI]: 'list',
    [ColumnType.CONTACT]: 'user',
    [ColumnType.PREDECESSOR]: 'git-branch',
    [ColumnType.AUTO_NUMBER]: 'binary',
    [ColumnType.FORMULA]: 'function-square',
    [ColumnType.SYMBOL]: 'star',
    [ColumnType.ATTACHMENT]: 'paperclip',
};
// ============================================================================
// Column Type Display Names
// ============================================================================
export const COLUMN_TYPE_NAMES = {
    [ColumnType.TEXT]: 'Text',
    [ColumnType.NUMBER]: 'Number',
    [ColumnType.DATE]: 'Date',
    [ColumnType.DATETIME]: 'Date & Time',
    [ColumnType.DURATION]: 'Duration',
    [ColumnType.PERCENT]: 'Percent',
    [ColumnType.CURRENCY]: 'Currency',
    [ColumnType.CHECKBOX]: 'Checkbox',
    [ColumnType.DROPDOWN_SINGLE]: 'Single Select',
    [ColumnType.DROPDOWN_MULTI]: 'Multi Select',
    [ColumnType.CONTACT]: 'Contact',
    [ColumnType.PREDECESSOR]: 'Predecessor',
    [ColumnType.AUTO_NUMBER]: 'Auto Number',
    [ColumnType.FORMULA]: 'Formula',
    [ColumnType.SYMBOL]: 'Symbol',
    [ColumnType.ATTACHMENT]: 'Attachment',
};
/**
 * Default formatter options.
 */
export const DEFAULT_FORMATTER_OPTIONS = {
    decimalPlaces: 2,
    thousandsSeparator: true,
    currencyCode: 'USD',
    currencySymbol: '$',
    currencyPosition: 'before',
    dateFormat: 'yyyy-MM-dd',
    timeFormat: 'HH:mm',
    timezone: 'UTC',
    durationFormat: 'days',
    hoursPerDay: 8,
    daysPerWeek: 5,
    locale: 'en-US',
};
/**
 * Column type formatters.
 * Note: These are simplified implementations. Full implementations would handle
 * localization, edge cases, and complex formatting rules.
 */
export const COLUMN_TYPE_FORMATTERS = {
    [ColumnType.TEXT]: {
        format: (value) => (value == null ? '' : String(value)),
        parse: (input) => input,
        validate: (value) => value == null || typeof value === 'string',
    },
    [ColumnType.NUMBER]: {
        format: (value, options = {}) => {
            if (value == null)
                return '';
            const num = Number(value);
            if (isNaN(num))
                return '';
            const { decimalPlaces = 2, thousandsSeparator = true, locale = 'en-US' } = options;
            return num.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: decimalPlaces,
                useGrouping: thousandsSeparator,
            });
        },
        parse: (input) => {
            const cleaned = input.replace(/[^\d.-]/g, '');
            const num = parseFloat(cleaned);
            return isNaN(num) ? null : num;
        },
        validate: (value) => value == null || (typeof value === 'number' && !isNaN(value)),
    },
    [ColumnType.DATE]: {
        format: (value, options = {}) => {
            if (value == null)
                return '';
            const date = value instanceof Date ? value : new Date(value);
            if (isNaN(date.getTime()))
                return '';
            return date.toISOString().split('T')[0];
        },
        parse: (input) => {
            const date = new Date(input);
            return isNaN(date.getTime()) ? null : date;
        },
        validate: (value) => {
            if (value == null)
                return true;
            const date = value instanceof Date ? value : new Date(value);
            return !isNaN(date.getTime());
        },
    },
    [ColumnType.DATETIME]: {
        format: (value, options = {}) => {
            if (value == null)
                return '';
            const date = value instanceof Date ? value : new Date(value);
            if (isNaN(date.getTime()))
                return '';
            return date.toISOString().replace('T', ' ').substring(0, 16);
        },
        parse: (input) => {
            const date = new Date(input);
            return isNaN(date.getTime()) ? null : date;
        },
        validate: (value) => {
            if (value == null)
                return true;
            const date = value instanceof Date ? value : new Date(value);
            return !isNaN(date.getTime());
        },
    },
    [ColumnType.DURATION]: {
        format: (value, options = {}) => {
            if (value == null)
                return '';
            const days = Number(value);
            if (isNaN(days))
                return '';
            const { durationFormat = 'days', hoursPerDay = 8 } = options;
            switch (durationFormat) {
                case 'hours':
                    return `${days * hoursPerDay}h`;
                case 'weeks':
                    return `${(days / 5).toFixed(1)}w`;
                case 'months':
                    return `${(days / 20).toFixed(1)}m`;
                default:
                    return `${days}d`;
            }
        },
        parse: (input) => {
            const match = input.match(/^([\d.]+)\s*(h|d|w|m)?$/i);
            if (!match)
                return null;
            const value = parseFloat(match[1]);
            const unit = (match[2] || 'd').toLowerCase();
            switch (unit) {
                case 'h':
                    return value / 8;
                case 'w':
                    return value * 5;
                case 'm':
                    return value * 20;
                default:
                    return value;
            }
        },
        validate: (value) => value == null || (typeof value === 'number' && value >= 0),
    },
    [ColumnType.PERCENT]: {
        format: (value) => {
            if (value == null)
                return '';
            const num = Number(value);
            if (isNaN(num))
                return '';
            return `${num}%`;
        },
        parse: (input) => {
            const cleaned = input.replace('%', '').trim();
            const num = parseFloat(cleaned);
            return isNaN(num) ? null : Math.max(0, Math.min(100, num));
        },
        validate: (value) => {
            if (value == null)
                return true;
            const num = Number(value);
            return !isNaN(num) && num >= 0 && num <= 100;
        },
    },
    [ColumnType.CURRENCY]: {
        format: (value, options = {}) => {
            if (value == null)
                return '';
            const num = Number(value);
            if (isNaN(num))
                return '';
            const { currencySymbol = '$', currencyPosition = 'before', decimalPlaces = 2, locale = 'en-US', } = options;
            const formatted = num.toLocaleString(locale, {
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces,
            });
            return currencyPosition === 'before' ? `${currencySymbol}${formatted}` : `${formatted}${currencySymbol}`;
        },
        parse: (input) => {
            const cleaned = input.replace(/[^\d.-]/g, '');
            const num = parseFloat(cleaned);
            return isNaN(num) ? null : num;
        },
        validate: (value) => value == null || (typeof value === 'number' && !isNaN(value)),
    },
    [ColumnType.CHECKBOX]: {
        format: (value) => (value ? 'true' : 'false'),
        parse: (input) => input.toLowerCase() === 'true' || input === '1',
        validate: (value) => value == null || typeof value === 'boolean',
    },
    [ColumnType.DROPDOWN_SINGLE]: {
        format: (value) => (value == null ? '' : String(value)),
        parse: (input) => input,
        validate: () => true,
    },
    [ColumnType.DROPDOWN_MULTI]: {
        format: (value) => {
            if (value == null)
                return '';
            if (Array.isArray(value))
                return value.join(', ');
            return String(value);
        },
        parse: (input) => input.split(',').map((s) => s.trim()),
        validate: (value) => value == null || Array.isArray(value),
    },
    [ColumnType.CONTACT]: {
        format: (value) => {
            if (value == null)
                return '';
            if (Array.isArray(value))
                return value.map((v) => (typeof v === 'object' ? v.displayName : v)).join(', ');
            if (typeof value === 'object')
                return value.displayName || '';
            return String(value);
        },
        parse: (input) => input,
        validate: () => true,
    },
    [ColumnType.PREDECESSOR]: {
        format: (value) => {
            if (value == null)
                return '';
            if (Array.isArray(value)) {
                return value
                    .map((p) => {
                    if (typeof p === 'object') {
                        const { rowNumber, type, lag } = p;
                        let str = `${rowNumber}${type.toUpperCase()}`;
                        if (lag)
                            str += lag > 0 ? `+${lag}d` : `${lag}d`;
                        return str;
                    }
                    return String(p);
                })
                    .join(', ');
            }
            return String(value);
        },
        parse: (input) => {
            // Parse predecessor strings like "1FS", "2SS+2d", "3FF-1d"
            const parts = input.split(',').map((s) => s.trim());
            return parts.map((part) => {
                const match = part.match(/^(\d+)(FS|SS|FF|SF)?([+-]\d+d)?$/i);
                if (!match)
                    return { rowNumber: parseInt(part, 10), type: 'fs', lag: 0 };
                return {
                    rowNumber: parseInt(match[1], 10),
                    type: (match[2] || 'fs').toLowerCase(),
                    lag: match[3] ? parseInt(match[3], 10) : 0,
                };
            });
        },
        validate: () => true,
    },
    [ColumnType.AUTO_NUMBER]: {
        format: (value, options = {}) => {
            if (value == null)
                return '';
            return String(value);
        },
        parse: () => null, // Auto-number cannot be parsed from user input
        validate: (value) => value == null || typeof value === 'number',
    },
    [ColumnType.FORMULA]: {
        format: (value) => (value == null ? '' : String(value)),
        parse: () => null, // Formula results cannot be parsed
        validate: () => true,
    },
    [ColumnType.SYMBOL]: {
        format: (value) => (value == null ? '' : String(value)),
        parse: (input) => input,
        validate: () => true,
    },
    [ColumnType.ATTACHMENT]: {
        format: (value) => {
            if (value == null)
                return '';
            if (Array.isArray(value))
                return `${value.length} file(s)`;
            return '1 file';
        },
        parse: () => null, // Attachments cannot be parsed from text
        validate: () => true,
    },
};
export const COLUMN_TYPE_METADATA = {
    [ColumnType.TEXT]: {
        type: ColumnType.TEXT,
        name: 'Text',
        icon: 'type',
        defaultWidth: 200,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'basic',
    },
    [ColumnType.NUMBER]: {
        type: ColumnType.NUMBER,
        name: 'Number',
        icon: 'hash',
        defaultWidth: 100,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: true,
        category: 'number',
    },
    [ColumnType.DATE]: {
        type: ColumnType.DATE,
        name: 'Date',
        icon: 'calendar',
        defaultWidth: 120,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'date',
    },
    [ColumnType.DATETIME]: {
        type: ColumnType.DATETIME,
        name: 'Date & Time',
        icon: 'calendar-clock',
        defaultWidth: 160,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'date',
    },
    [ColumnType.DURATION]: {
        type: ColumnType.DURATION,
        name: 'Duration',
        icon: 'clock',
        defaultWidth: 100,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: true,
        category: 'number',
    },
    [ColumnType.PERCENT]: {
        type: ColumnType.PERCENT,
        name: 'Percent',
        icon: 'percent',
        defaultWidth: 80,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: true,
        category: 'number',
    },
    [ColumnType.CURRENCY]: {
        type: ColumnType.CURRENCY,
        name: 'Currency',
        icon: 'dollar-sign',
        defaultWidth: 120,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: true,
        category: 'number',
    },
    [ColumnType.CHECKBOX]: {
        type: ColumnType.CHECKBOX,
        name: 'Checkbox',
        icon: 'check-square',
        defaultWidth: 60,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'basic',
    },
    [ColumnType.DROPDOWN_SINGLE]: {
        type: ColumnType.DROPDOWN_SINGLE,
        name: 'Single Select',
        icon: 'chevron-down-circle',
        defaultWidth: 150,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'list',
    },
    [ColumnType.DROPDOWN_MULTI]: {
        type: ColumnType.DROPDOWN_MULTI,
        name: 'Multi Select',
        icon: 'list',
        defaultWidth: 200,
        editable: true,
        sortable: false,
        filterable: true,
        aggregatable: false,
        category: 'list',
    },
    [ColumnType.CONTACT]: {
        type: ColumnType.CONTACT,
        name: 'Contact',
        icon: 'user',
        defaultWidth: 180,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'list',
    },
    [ColumnType.PREDECESSOR]: {
        type: ColumnType.PREDECESSOR,
        name: 'Predecessor',
        icon: 'git-branch',
        defaultWidth: 150,
        editable: true,
        sortable: false,
        filterable: false,
        aggregatable: false,
        category: 'special',
    },
    [ColumnType.AUTO_NUMBER]: {
        type: ColumnType.AUTO_NUMBER,
        name: 'Auto Number',
        icon: 'binary',
        defaultWidth: 80,
        editable: false,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'special',
    },
    [ColumnType.FORMULA]: {
        type: ColumnType.FORMULA,
        name: 'Formula',
        icon: 'function-square',
        defaultWidth: 150,
        editable: false,
        sortable: true,
        filterable: true,
        aggregatable: true,
        category: 'special',
    },
    [ColumnType.SYMBOL]: {
        type: ColumnType.SYMBOL,
        name: 'Symbol',
        icon: 'star',
        defaultWidth: 80,
        editable: true,
        sortable: true,
        filterable: true,
        aggregatable: false,
        category: 'basic',
    },
    [ColumnType.ATTACHMENT]: {
        type: ColumnType.ATTACHMENT,
        name: 'Attachment',
        icon: 'paperclip',
        defaultWidth: 100,
        editable: false,
        sortable: false,
        filterable: false,
        aggregatable: false,
        category: 'special',
    },
};
/**
 * Get column types by category.
 */
export function getColumnTypesByCategory(category) {
    return Object.values(COLUMN_TYPE_METADATA)
        .filter((meta) => meta.category === category)
        .map((meta) => meta.type);
}
//# sourceMappingURL=columns.js.map